import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTimePickerComponent } from '@progress/kendo-angular-dateinputs';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { GridComponent } from '@progress/kendo-angular-grid';
import { ComponentBase } from '~/infrastructure/component-base';
import { UserSummary } from '~/models/administration/user';
import {
  StockCycleCountChunkDetailModel,
  StockCycleCountChunkModel,
  StockCycleCountTransactionModel,
  StockUpdateCycleCountDetailsModel
} from '~/models/modules/Stocks/Transactions/StockCycleCount/StockCycleCount';
import { FormatService } from '~/services/administration.ts/format';
import { LocationsService } from '~/services/modules/stocks/locations';
import { StockCycleCountService } from '~/services/modules/stocks/stock-cycle-count.service';
import { TransactionDateEditingService } from '~/services/modules/stocks/administration/transaction-date-editing';
import { SecurityService } from '~/services/security.service';
import { SharedService } from '~/services/sharedService';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutManagerService } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { PageState } from '~/shared/enums/stock-files.enum';
import { PageNames } from '~/models/pageNames';

@Component({
    selector: 'app-stock-cycle-count',
    templateUrl: './stock-cycle-count.html',
    styleUrls: ['./stock-cycle-count.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StockCycleCountComponent extends ComponentBase implements OnInit, OnDestroy {
  page: string = PageNames.StockCycleCount;
  title: string = 'Stock Cycle Count';
  showTextBox: boolean = false;
  @ViewChild('kendo_Start_Date_Time_Piker') kendoStartDateTimePiker: DateTimePickerComponent;
  @ViewChild('kendo_combo_accounts') kendoComboAccounts: ComboBoxComponent;
  @ViewChild('kendo_combo_sites') kendoComboSites: ComboBoxComponent;
  @ViewChild('grid') grid: GridComponent;

  _exportFileName: string = 'stock-cycle-count';
  _allSelected: boolean = false;
  _currentUser: UserSummary;
  _theme: string;
  _form: FormGroup;
  _invalidForm: boolean = false;
  _addClassActiveLocation: boolean = false;
  _addClassActiveGrid: boolean = false;
  _addClassActive3: boolean = false;
  _selectedLocations: any[] = [];
  _locations: any[] = [];
  _locationsFiltered: any[] = [];
  _datePickerFormat: string;
  _costFormat: string;
  _dateEnabled: boolean = false;
  loggedInSiteId: number = 0;
  _pageState: PageState;
  public _cycleCountGridData: StockCycleCountChunkDetailModel[] = [];
  _totalPercentage: number = 0;
  _transactionId: number = 0;
  _chunkId: number = 0;
  _percentageReportValue: number = 0;
  _processPercentage: number = 0;
  _maxChunkPercentage: number = 1;
  _isDrafted: boolean = false;

  _pdfColumns: any[] = [
    { field: 'stockItemCode', name: 'Item Code', width: 200 },
    { field: 'stockItemDescription', name: 'Description', width: 250 },
    { field: 'inventoryNumber', name: 'Inventory #', width: 200 },
    { field: 'expectedLocationName', name: 'Expected Location', width: 200 },
    { field: 'actualLocationName', name: 'Actual Location', width: 200 },
    { field: 'expectedQuantity', name: 'Expected Qty', width: 150 },
    { field: 'actualQuantity', name: 'Actual Qty', width: 150 }
  ];

  constructor(
    private _fb: FormBuilder,
    private _themeManager: ThemeManagerService,
    private _layoutComponent: LayoutComponent,
    private _layoutManager: LayoutManagerService,
    private _securityService: SecurityService,
    private _locationsService: LocationsService,
    private _formatService: FormatService,
    private _TransactionDateEditing: TransactionDateEditingService,
    private _stockCycleCountService: StockCycleCountService,
    private _sharedService: SharedService,
    private router: Router
  ) {
    super();
    this._themeManager.themeChange.subscribe((data) => {
      this._theme = data;
    });
    this._currentUser = this._securityService.currentUser;
  }

  ngOnInit(): void {
    this._initForm();
    const loggedInAccountSites = localStorage.getItem('loggedInAccountSites') ?? '';
    if (loggedInAccountSites) {
      try {
        this.loggedInSiteId = JSON.parse(loggedInAccountSites)[0]?.siteId || 0;
      } catch (e) {
        this.loggedInSiteId = 0;
      }
    }
    if (this.loggedInSiteId) {
      this._LoadLocation(this.loggedInSiteId);
    }
    this._datePickerFormat = this._formatService.dateFormat;
    this._costFormat = this._formatService.costFormatValue;
    this.getDateEnabledOption();
    this._LoadDraftedTransaction();
  }

  ngOnDestroy(): void {
    this._layoutManager.removeOverlay();
  }

  private _initForm(): void {
    this._form = this._fb.group({
      transactionDate: [this._sharedService.getTodayInUserTimezone(), Validators.required],
      percentage: [0.2, [Validators.required, Validators.min(0.01)]],
      percentageReport: [0],
      countType: [1, Validators.required],
      cycleName: ['']
    });
  }

  _LoadLocation(siteId: number): void {
    const siteModel = { siteId: siteId };
    this._locationsService.getLocationsByAccounts(siteModel).subscribe((data: any) => {
      this._locations = data || [];
      this._locationsFiltered = data || [];
      if (this._cycleCountGridData && this._cycleCountGridData.length > 0) {
        this._cycleCountGridData.forEach((c) => {
          c.locationsFiltered = this._locations;
          c.actualLocationName = this._locations?.find((l) => l.id === c.actualLocationId)?.code || c.actualLocationCode || c.actualLocationName;
          c.expectedLocationName = c.expectedLocationName || this._locations?.find((l) => l.id === c.expectedLocationId)?.code || c.expectedLocationCode;
        });
      }
    });
  }

  showTextbox(): void {
    this.showTextBox = true;
  }

  closeTextbox(target: any): void {
    this.showTextBox = false;
    if (target?.value) {
      this.title = target.value;
    }
  }

  _LoadDraftedTransaction(): void {
    this._stockCycleCountService.LoadDraftedTransaction().subscribe((res: any) => {
      if (res && res.id) {
        const rawDate = res.transactionDate;
        const validDate = rawDate && rawDate !== '0001-01-01T00:00:00' && new Date(rawDate).getFullYear() > 1970
          ? new Date(rawDate)
          : this._sharedService.getTodayInUserTimezone();
        this._totalPercentage = res.percentage || 0;
        this._maxChunkPercentage = res.maxChunkPercentage != null ? res.maxChunkPercentage : (1 - this._totalPercentage / 100);
        this._transactionId = res.id;
        // Read single draft chunk from lastCycleCountChunk
        const chunk = res.lastCycleCountChunk;
        if (chunk) {
          this._chunkId = chunk.id || 0;
          this._processPercentage = chunk.percentage || 0;
          this._form.patchValue({
            countType: res.countType,
            percentage: (chunk.percentage || 0) / 100,
            cycleName: chunk.name,
            transactionDate: validDate
          });
          const rawDetails = chunk.stockCycleCountChunkDetails || chunk.cycleCountChunkDetails || [];
          this._cycleCountGridData = rawDetails.map((c: any) => ({
            ...c,
            locationsFiltered: this._locations,
            stockItemId: c.stockItemId || c.stockLocation?.stockItemId,
            stockItemCode: c.stockItemCode || c.stockLocation?.stockItem?.number,
            stockItemDescription: c.stockItemDescription || c.stockLocation?.stockItem?.description,
            inventoryNumber: c.inventoryNumber || c.stockLocation?.inventoryNumber,
            actualLocationId: c.actualLocationId || c.expectedLocationId,
            actualLocationName: this._locations?.find((l) => l.id === (c.actualLocationId || c.expectedLocationId))?.code || c.actualLocationCode || c.actualLocationName,
            actualLocationCode: c.actualLocationCode || this._locations?.find((l) => l.id === (c.actualLocationId || c.expectedLocationId))?.code,
            expectedLocationCode: c.expectedLocationCode || c.expectedLocation?.code || this._locations?.find((l) => l.id === c.expectedLocationId)?.code,
            expectedLocationName: c.expectedLocationName || c.expectedLocationCode || c.expectedLocation?.code || c.expectedLocation?.description || this._locations?.find((l) => l.id === c.expectedLocationId)?.code,
            expectedQuantity: c.expectedQuantity ?? 0,
            actualQuantity: c.actualQuantity ?? c.expectedQuantity ?? 0,
            varianceQuantity: (c.actualQuantity ?? c.expectedQuantity ?? 0) - (c.expectedQuantity ?? 0),
            variance: ((c.actualQuantity ?? c.expectedQuantity ?? 0) !== (c.expectedQuantity ?? 0)) || (c.actualLocationId !== c.expectedLocationId)
          }));
          this._isDrafted = true;
          this._dateEnabled = false;
          this._form.get('transactionDate')?.disable();
          this._form.get('countType')?.disable();
          this._form.get('percentage')?.disable();
          this._form.get('cycleName')?.disable();
        } else {
          this._chunkId = 0;
          this._processPercentage = 0;
          this._form.patchValue({
            countType: res.countType || 1,
            transactionDate: validDate
          });
          this._cycleCountGridData = [];
          this._isDrafted = false;

          if (this._totalPercentage >= 100 || this._maxChunkPercentage <= 0) {
            this._form.patchValue({ percentage: 0 });
            this._form.get('percentage')?.disable();
            this._form.get('countType')?.disable();
          } else {
            const defaultPct = Math.min(0.2, this._maxChunkPercentage);
            this._form.patchValue({ percentage: defaultPct });
            this._form.get('countType')?.enable();
            this._form.get('percentage')?.enable();
          }
        }
      }
    });
  }

  getDateEnabledOption(): void {
    this._TransactionDateEditing.getTransactionDateEditing('get-data').subscribe((x: any) => {
      if (x != undefined && x != null) {
        this._dateEnabled = x.transactionDateEditing;
        if (!this._dateEnabled) {
          this._form.get('transactionDate')?.disable();
        }
      }
    });
  }

  _startDateSubmited(): void {
    setTimeout(() => {
      this.kendoStartDateTimePiker?.blur();
    });
  }

  _startDateOpened(): void {
    this._addClassActiveLocation = false;
    this._addClassActiveGrid = false;
  }

  _listboxClicked(): void {
    this._addClassActiveLocation = true;
    if (this.kendoStartDateTimePiker) {
      this.kendoStartDateTimePiker.toggle(false);
      this.kendoStartDateTimePiker.blur();
    }
    this._addClassActiveGrid = false;
    this._addClassActive3 = false;
  }

  _step3Clicked(): void {
    this._addClassActive3 = true;
    if (this.kendoStartDateTimePiker) {
      this.kendoStartDateTimePiker.toggle(false);
      this.kendoStartDateTimePiker.blur();
    }
    this._addClassActiveGrid = false;
    this._addClassActiveLocation = false;
  }

  _gridclicked(): void {
    this._addClassActiveGrid = true;
    this._addClassActiveLocation = false;
    this._addClassActive3 = false;
  }

  _startCycleCount(): void {
    this._form.get('cycleName')?.clearValidators();
    this._form.get('cycleName')?.updateValueAndValidity();

    if (this._form.invalid) {
      this._invalidForm = true;
      return;
    }

    const pct = this._form.get('percentage')?.value;
    const calcPct = Math.round(pct * 100);
    this._processPercentage = calcPct;

    const countType = this._form.get('countType')?.value || 1;
    this._stockCycleCountService
      .LoadDataRandomly(calcPct, this._transactionId, countType)
      .subscribe((data: any) => {
        const items = Array.isArray(data) ? data : data?.data || [];
        this._cycleCountGridData = items.map((item: any) => ({
          ...item,
          locationsFiltered: this._locations,
          actualQuantity: item.actualQuantity ?? item.expectedQuantity ?? 0,
          expectedLocationCode: item.expectedLocationCode || item.expectedLocation?.code || this._locations?.find((l) => l.id === item.expectedLocationId)?.code,
          expectedLocationName: item.expectedLocationName || item.expectedLocationCode || item.expectedLocation?.code || item.expectedLocation?.description || this._locations?.find((l) => l.id === item.expectedLocationId)?.code,
          actualLocationId: item.actualLocationId || item.expectedLocationId,
          actualLocationCode: item.actualLocationCode || item.actualLocation?.code || this._locations?.find((l) => l.id === (item.actualLocationId || item.expectedLocationId))?.code,
          actualLocationName: item.actualLocationName || this._locations?.find((l) => l.id === (item.actualLocationId || item.expectedLocationId))?.code || item.actualLocationCode || item.expectedLocationName,
          variance: false,
          varianceQuantity: 0
        }));

        if (this._cycleCountGridData.length > 0) {
          this._form.get('transactionDate')?.disable();
          this._form.get('countType')?.disable();
          this._form.get('percentage')?.disable();
        }
      });
  }

  onActualQuantityChange(dataItem: StockCycleCountChunkDetailModel, newQty: number): void {
    dataItem.actualQuantity = newQty;
    const expected = dataItem.expectedQuantity ?? 0;
    dataItem.varianceQuantity = newQty - expected;
    dataItem.variance = dataItem.varianceQuantity !== 0;
  }

  getSelectedLocation(item: StockCycleCountChunkDetailModel): any {
    return this._locations?.find((l) => l.id === item.actualLocationId);
  }

  onLocationFilterChange(value: string, dataItem: any): void {
    if (!value) {
      dataItem.locationsFiltered = this._locations;
    } else {
      dataItem.locationsFiltered = this._locations?.filter(
        (l: any) => l.code && l.code.toLowerCase().indexOf(value.toLowerCase()) !== -1
      ) || [];
    }
  }

  onLocationChange(location: any, item: StockCycleCountChunkDetailModel): void {
    item.locationsFiltered = this._locations;
    if (location && typeof location === 'object' && location.id) {
      item.actualLocationId = location.id;
      item.actualLocationName = location.code || location.name;
      item.actualLocationCode = location.code;
    } else if (typeof location === 'number') {
      const loc = this._locations?.find((l) => l.id === location);
      item.actualLocationId = location;
      item.actualLocationName = loc?.code || loc?.name;
      item.actualLocationCode = loc?.code;
    } else if (typeof location === 'string') {
      const loc = this._locations?.find((l) => l.code?.toLowerCase() === location.toLowerCase());
      if (loc) {
        item.actualLocationId = loc.id;
        item.actualLocationName = loc.code || loc.name;
        item.actualLocationCode = loc.code;
      }
    } else {
      item.actualLocationId = null;
      item.actualLocationName = '';
      item.actualLocationCode = '';
    }
    item.variance = (item.actualQuantity !== item.expectedQuantity) || (item.actualLocationId !== item.expectedLocationId);
  }

  _process(): void {
    if (!this._form.get('cycleName')?.hasValidator(Validators.required)) {
      this._form.get('cycleName')?.setValidators([Validators.required]);
      this._form.get('cycleName')?.updateValueAndValidity();
    }

    if (this._form.invalid) {
      this._invalidForm = true;
      return;
    }
    this._invalidForm = false;

    if (!this._isDrafted) {
      const model: StockCycleCountChunkModel = {
        name: this._form.get('cycleName')?.value,
        percentage: this._processPercentage,
        stockCycleCountChunkDetails: this._cycleCountGridData,
        cycleCountTransactionId: this._transactionId,
        isDraft: false,
        stockCycleCount: {
          countType: this._form.get('countType')?.value || 1,
          transactionDate: this._form.get('transactionDate')?.value
        }
      };
      this._stockCycleCountService.Process(model).subscribe((res: any) => {
        const msg = res?.data?.message || res?.message || 'Cycle count chunk processed successfully.';
        this._onClear();
        this.setfinalyPupmode(msg);
      }, error => {
        const msg = error?.message || 'Error occurred';
        this.setfinalyPupmode(msg);
      });
    } else {
      const model: StockUpdateCycleCountDetailsModel = {
        stockCycleCountChunkDetails: this._cycleCountGridData,
        transactionId: this._transactionId,
        chunkId: this._chunkId
      };
      this._stockCycleCountService.UpdateDetails(model).subscribe((res: any) => {
        const msg = res?.data?.message || res?.message || 'Cycle count details updated successfully.';
        this._onClear();
        this.setfinalyPupmode(msg);
      }, error => {
        const msg = error?.message || 'Error occurred';
        this.setfinalyPupmode(msg);
      });
    }
  }

  _save(): void {
    if (!this._form.get('cycleName')?.hasValidator(Validators.required)) {
      this._form.get('cycleName')?.setValidators([Validators.required]);
      this._form.get('cycleName')?.updateValueAndValidity();
    }

    if (this._form.invalid) {
      this._invalidForm = true;
      return;
    }
    this._invalidForm = false;

    if (!this._isDrafted) {
      const model: StockCycleCountChunkModel = {
        name: this._form.get('cycleName')?.value,
        percentage: this._processPercentage,
        stockCycleCountChunkDetails: this._cycleCountGridData,
        cycleCountTransactionId: this._transactionId,
        isDraft: true,
        stockCycleCount: {
          countType: this._form.get('countType')?.value || 1,
          transactionDate: this._form.get('transactionDate')?.value
        }
      };
      this._stockCycleCountService.Process(model).subscribe((res: any) => {
        const msg = res?.data?.message || res?.message || 'Draft saved successfully.';
        this._onClear();
        this.setfinalyPupmode(msg);
      }, error => {
        const msg = error?.message || 'Error occurred';
        this.setfinalyPupmode(msg);
      });
    } else {
      const model: StockUpdateCycleCountDetailsModel = {
        stockCycleCountChunkDetails: this._cycleCountGridData,
        transactionId: this._transactionId,
        chunkId: this._chunkId
      };
      this._stockCycleCountService.UpdateDetails(model).subscribe((res: any) => {
        const msg = res?.data?.message || res?.message || 'Draft saved successfully.';
        this._onClear();
        this.setfinalyPupmode(msg);
      }, error => {
        const msg = error?.message || 'Error occurred';
        this.setfinalyPupmode(msg);
      });
    }
  }

  private setfinalyPupmode(msg: any): void {
    this._layoutComponent.openPopup({
      title: 'Alert',
      message: msg,
      withoutConfirm: true
    });
  }

  _onClear(): void {
    this._invalidForm = false;
    this._cycleCountGridData = [];
    this._selectedLocations = [];
    this._totalPercentage = 0;
    this._transactionId = 0;
    this._chunkId = 0;
    this._percentageReportValue = 0;
    this._processPercentage = 0;
    this._maxChunkPercentage = 1;
    this._isDrafted = false;
    this._form.reset();
    this._initForm();
    if (this.loggedInSiteId) {
      this._LoadLocation(this.loggedInSiteId);
    }
    this.getDateEnabledOption();
    this._LoadDraftedTransaction();
  }

  _reset(): void {
    this._onClear();
  }

  _print(): void {
    this._export();
  }

  _export(): void {
    this.grid?.saveAsPDF();
  }

  percentageChange(e: any): void {
    setTimeout(() => {
      if (this._maxChunkPercentage <= 0) {
        this._form.controls['percentage']?.setValue(0);
        return;
      }
      let p = this._form.get('percentage')?.value;
      if (p > 1) p = p / 100;
      if (p > this._maxChunkPercentage) p = this._maxChunkPercentage;
      if (p < 0.01) p = 0.01;
      this._form.controls['percentage'].setValue(p);
    });
  }
}
